<template>
  <button class="btn" :disabled="processing ? true : undefined" @click.prevent="handleFavorite()">
    <i v-if="favoriteStatus" class="material-symbols-outlined fill" aria-hidden="true">favorite</i>
    <i v-else class="material-symbols-outlined" aria-hidden="true">favorite</i>
    {{ favoriteText }}
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useFavoritesStore } from '@/stores/favorites'
import { useModalsStore } from '@/stores/modals'
import { fbApi, FbApiError } from '@/utils/http-api'

/**
 * Toggle favorite status for a single favoritable type / id.
 */
export default defineComponent({
  name: 'FavoriteButton',
  props: {
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      type: String as PropType<'workout' | 'article' | 'wellness_video'>,
      default: 'workout',
      validator: (val: string) => ['workout', 'article', 'wellness_video'].includes(val),
    },
  },
  emits: ['favorite-updated'],
  data() {
    return {
      processing: false,
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['user']),
    ...mapState(useFavoritesStore, ['isFavorite']),

    // if processing, instantly flip favorite status in UI
    // while XHR is processing
    favoriteStatus() {
      const isFavoriteNow = this.isFavorite({
        favoritableId: this.entityId,
        favoritableType: this.entityType,
      })

      if (this.processing) {
        return !isFavoriteNow
      }

      return isFavoriteNow
    },

    favoriteText() {
      return this.favoriteStatus ? 'Remove from Favorites' : 'Add to Your Favorites'
    },
  },
  methods: {
    ...mapActions(useModalsStore, ['showLoginModal']),
    ...mapActions(useFavoritesStore, ['addFavorite', 'removeFavorite']),

    // allow parent components to react to change event
    emitUpdated() {
      this.$emit('favorite-updated', {
        favoritableId: this.entityId,
        favoritableType: this.entityType,
        isFavorite: this.favoriteStatus,
      })
    },

    // xhr for add or delete favorite
    async handleFavorite() {
      if (!this.user.isLoggedIn) {
        this.showLoginModal()
        return
      }

      // do not double submit
      if (this.processing) {
        return
      }

      this.processing = true
      let deleteRequest = false
      const payload = {
        favoritableType: this.entityType,
        favoritableId: this.entityId,
      }

      const isFavoriteNow = this.isFavorite(payload)

      try {
        let res = null

        if (!isFavoriteNow) {
          // add favorite
          res = await fbApi.post('/favorites', {
            json: payload,
          })

          if (!res || !res.body || !res.body.data) {
            throw Error('Favorite fetch missing response')
          }

          this.addFavorite(payload)
        } else {
          // remove favorite
          deleteRequest = true

          await fbApi.delete(`/favorites/${this.entityType}/${this.entityId}`)

          this.removeFavorite(payload)
        }

        // pass up the food chain
        this.emitUpdated()
      } catch (e) {
        //  if delete and 404, still toggle favorite
        if (deleteRequest && e instanceof FbApiError && e.status === 404) {
          this.removeFavorite(payload)
          this.emitUpdated()
        }
      } finally {
        this.processing = false
      }
    },
  },
})
</script>
